import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useAutomata } from '../contexts/AutomataContext';
import { useClipboard } from '../hooks/useClipboard';
import { snap } from '../utils/automataHelpers';
import { exportAutomatonAsDocx } from '../utils/docxExport';
import { TooltipProvider } from '../components/ui/tooltip';
import { Loader2, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { BugReportButton } from '../components/BugReportButton';

import {
  DashboardHeader, Sidebar, SimulatorBar,
  CanvasToolbar, BottomPanels, HintsPanel, DashboardDialogs,
} from '../components/dashboard';
import { HelpPane } from '../components/dashboard/HelpPane';
import { FloatingStack } from '../components/dashboard/FloatingStack';
import { FloatingTape } from '../components/dashboard/FloatingTape';
import { PdaTransitionEditor } from '../components/dashboard/PdaTransitionEditor';
import { TmTransitionEditor } from '../components/dashboard/TmTransitionEditor';

import { CanvasContextMenu, buildStateMenuItems, buildTransitionMenuItems } from '../components/dashboard/CanvasContextMenu';

// Lazy-load canvas (large component)
import AutomataCanvas from '../components/AutomataCanvas';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, isAdmin, loginWithGoogle } = useAuth();
  const {
    automata, currentAutomaton, loading, saving, isGuest,
    createAutomaton, loadAutomaton, deleteAutomaton, duplicateAutomaton,
    exportAutomaton, importAutomaton, updateCurrentAutomaton,
    renameAutomaton, togglePinAutomaton, addState, updateState, deleteState,
    addTransition, updateTransition, deleteTransition,
    undo, redo, canUndo, canRedo,
    verifyAutomaton,
    groups, createGroup, renameGroup, deleteGroup, reorderGroups, moveAutomaton, reorderAutomata,
  } = useAutomata();

  const { copyLanguageDescription, copySpecification, copyTransitionFunction } = useClipboard(automata, currentAutomaton);

  // ==========================================================================
  // UI State
  // ==========================================================================
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(() => parseInt(localStorage.getItem('sidebarWidth') || '240'));
  const [isImpersonating] = useState(() => !!localStorage.getItem('impersonating'));
  const [showBottomPanels, setShowBottomPanels] = useState(() => localStorage.getItem('showBottomPanels') !== 'false');
  const [panelHeight, setPanelHeight] = useState(() => parseInt(localStorage.getItem('panelHeight') || '180'));
  const [showHintsPanel, setShowHintsPanel] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(() => localStorage.getItem('snapToGrid') !== 'false');
  const [selectedState, setSelectedState] = useState(null);
  const [selectedTransition, setSelectedTransition] = useState(null);
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [canvasContextMenu, setCanvasContextMenu] = useState(null); // { type, id, screenX, screenY, items }

  // Blocks a mutating canvas action while viewing as another user, showing
  // a consistent explanation instead of a confusing no-op or 403 toast.
  const blockReadOnly = useCallback(() => {
    if (isImpersonating) {
      toast.error('Read-only while viewing as another user.', { id: 'readonly-impersonation' });
      return true;
    }
    return false;
  }, [isImpersonating]);

  // Guarded version of updateCurrentAutomaton for anything that edits
  // automaton-level fields outside the canvas itself (language description,
  // PDA/TM settings, saved test strings) -- all of these funnel through
  // this one function, so wrapping it here covers them all.
  const guardedUpdateCurrentAutomaton = useCallback((updates) => {
    if (blockReadOnly()) return;
    updateCurrentAutomaton(updates);
  }, [updateCurrentAutomaton, blockReadOnly]);

  // Dialog state
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [showSidebarRenameDialog, setShowSidebarRenameDialog] = useState(false);
  const [sidebarRenameId, setSidebarRenameId] = useState(null);
  const [sidebarRenameName, setSidebarRenameName] = useState('');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importGroupId, setImportGroupId] = useState(null);
  const [showImportWarningDialog, setShowImportWarningDialog] = useState(false);
  const [showClearCanvasDialog, setShowClearCanvasDialog] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [pendingImportAfterAuth, setPendingImportAfterAuth] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [verifyMode, setVerifyMode] = useState('strict');
  const [verificationResult, setVerificationResult] = useState(null);
  const [showNewGroupDialog, setShowNewGroupDialog] = useState(false);
  const [showGroupRenameDialog, setShowGroupRenameDialog] = useState(false);
  const [groupRenameId, setGroupRenameId] = useState(null);
  const [groupRenameName, setGroupRenameName] = useState('');
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, automatonId: null });
  const [showHelpPane, setShowHelpPane] = useState(false);

  // PDA transition editor
  const [pdaEditorState, setPdaEditorState] = useState(null); // { transitionId, x, y }

  // Simulation state
  const [simInput, setSimInput] = useState('');
  const [simResult, setSimResult] = useState(null);
  const [simStep, setSimStep] = useState(-1);
  const [simAutoRun, setSimAutoRun] = useState(false);
  const [simAnimating, setSimAnimating] = useState(false);
  const [animOverride, setAnimOverride] = useState(null);
  const [lastTakenTransitions, setLastTakenTransitions] = useState([]);

  // Refs
  const canvasRef = useRef(null);
  const canvasPositions = useRef({});
  const animFrameRef = useRef(null);

  // ==========================================================================
  // Persistence effects
  // ==========================================================================
  useEffect(() => { localStorage.setItem('showBottomPanels', showBottomPanels); }, [showBottomPanels]);
  useEffect(() => { localStorage.setItem('panelHeight', panelHeight); }, [panelHeight]);
  useEffect(() => { localStorage.setItem('snapToGrid', snapToGrid); }, [snapToGrid]);

  // Close context menu on click outside
  const closeContextMenu = useCallback(() => setContextMenu(prev => ({ ...prev, visible: false })), []);
  useEffect(() => {
    if (contextMenu.visible) {
      const handler = () => closeContextMenu();
      window.addEventListener('click', handler);
      return () => window.removeEventListener('click', handler);
    }
  }, [contextMenu.visible, closeContextMenu]);

  // Keyboard shortcuts (undo/redo, delete)
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); if (!isImpersonating) undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); if (!isImpersonating) redo(); }
      if ((e.key === 'Delete' || e.key === 'Backspace') && currentAutomaton) {
        if (canvasRef.current?.isEditing?.()) return;
        if (isImpersonating) return;
        if (selectedTransition) { deleteTransition(selectedTransition); setSelectedTransition(null); }
        else if (selectedState) { deleteState(selectedState); setSelectedState(null); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo, selectedState, selectedTransition, deleteState, deleteTransition, currentAutomaton, isImpersonating]);

  // Check for pending guest automaton import (after Google OAuth redirect)
  useEffect(() => {
    const pendingData = localStorage.getItem('pendingGuestAutomaton');
    if (pendingData && !isGuest) {
      localStorage.removeItem('pendingGuestAutomaton');
      try {
        const data = JSON.parse(pendingData);
        importAutomaton(data);
      } catch {}
    }
    if (localStorage.getItem('pendingOpenImportDialog') === 'true') {
      localStorage.removeItem('pendingOpenImportDialog');
      setTimeout(() => setShowImportDialog(true), 500);
    }
  }, [isGuest, importAutomaton]);

  // ==========================================================================
  // Automaton CRUD handlers
  // ==========================================================================
  const handleNewAutomaton = async (groupId, type = 'DFA') => {
    try {
      await createAutomaton('Untitled Automaton', type, groupId || groups[0]?.group_id);
    } catch (error) {
      console.error('Failed to create automaton:', error);
    }
  };

  const handleLoadAutomaton = async (automatonId) => {
    try {
      if (currentAutomaton?.automaton_id) {
        canvasPositions.current[currentAutomaton.automaton_id] = canvasRef.current?.getViewState?.();
      }
      await loadAutomaton(automatonId);
      setSelectedState(null);
      setSelectedTransition(null);
      setShowSimulator(false);
      setSimInput('');
      handleSimReset();
      setTimeout(() => {
        const saved = canvasPositions.current[automatonId];
        if (saved) canvasRef.current?.setViewState?.(saved);
        else canvasRef.current?.resetView?.();
      }, 0);
    } catch (error) {
      console.error('Failed to load automaton:', error);
    }
  };

  const handleDeleteAutomaton = async (automatonId, e) => {
    if (e) e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this automaton?')) {
      try { await deleteAutomaton(automatonId); } catch (error) { console.error('Failed to delete:', error); }
    }
  };

  const handleDuplicateAutomaton = async (automatonId) => {
    try { await duplicateAutomaton(automatonId); } catch (error) { console.error('Failed to duplicate:', error); }
  };

  const handleExportAutomaton = (automatonId) => {
    const target = automata.find(a => a.automaton_id === automatonId);
    if (!target) return;
    const data = { name: target.name, type: target.type, states: target.states, alphabet: target.alphabet, transitions: target.transitions, language_description: target.language_description || '', test_accept_strings: target.test_accept_strings || [], test_reject_strings: target.test_reject_strings || [] };
    if (target.type === 'PDA') { data.initial_stack_symbol = target.initial_stack_symbol || 'Z'; data.acceptance_mode = target.acceptance_mode || 'final_state'; }
    if (target.type === 'TM') { data.blank_symbol = target.blank_symbol || '\u25A1'; data.step_limit = target.step_limit || 10000; }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${target.name.replace(/\s+/g, '_')}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Automaton exported');
  };

  const handleExport = async () => {
    const data = await exportAutomaton();
    if (data) {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${currentAutomaton.name.replace(/\s+/g, '_')}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Automaton exported');
    }
  };

  // ==========================================================================
  // Rename handlers
  // ==========================================================================
  const handleRenameSubmit = async () => {
    if (!currentAutomaton || !renameName.trim()) return;
    await renameAutomaton(currentAutomaton.automaton_id, renameName.trim());
    setShowRenameDialog(false);
    toast.success('Automaton renamed');
  };

  const openSidebarRenameDialog = (automatonId) => {
    const a = automata.find(x => x.automaton_id === automatonId);
    if (a) { setSidebarRenameId(automatonId); setSidebarRenameName(a.name); setShowSidebarRenameDialog(true); }
  };

  const handleSidebarRenameSubmit = async () => {
    if (!sidebarRenameId || !sidebarRenameName.trim()) return;
    await renameAutomaton(sidebarRenameId, sidebarRenameName.trim());
    setShowSidebarRenameDialog(false); setSidebarRenameId(null); setSidebarRenameName('');
    toast.success('Automaton renamed');
  };

  // ==========================================================================
  // Import handlers
  // ==========================================================================
  const handleImportSubmit = async () => {
    try {
      const data = JSON.parse(importJson);

      // Group export: { type: 'automata_group', automata: [...] }
      if (data.type === 'automata_group' && Array.isArray(data.automata)) {
        let count = 0;
        for (const item of data.automata) {
          if (importGroupId) item.group_id = importGroupId;
          await importAutomaton(item);
          count++;
        }
        setShowImportDialog(false); setImportJson(''); setImportGroupId(null);
        toast.success(`Imported ${count} automata`);
        return;
      }

      // Array of automata (bare array)
      if (Array.isArray(data)) {
        let count = 0;
        for (const item of data) {
          if (importGroupId) item.group_id = importGroupId;
          await importAutomaton(item);
          count++;
        }
        setShowImportDialog(false); setImportJson(''); setImportGroupId(null);
        toast.success(`Imported ${count} automata`);
        return;
      }

      // Single automaton
      if (importGroupId) data.group_id = importGroupId;
      await importAutomaton(data);
      setShowImportDialog(false); setImportJson(''); setImportGroupId(null);
    } catch { toast.error('Invalid JSON format'); }
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => setImportJson(evt.target.result);
    reader.readAsText(file);
  };

  const handleGuestImportClick = () => {
    if (currentAutomaton?.states?.length > 0) setShowImportWarningDialog(true);
    else setShowImportDialog(true);
  };

  const handleExportThenImport = async () => { await handleExport(); setShowImportWarningDialog(false); setShowImportDialog(true); };
  const handleSignInThenImport = () => { setPendingImportAfterAuth(true); setShowImportWarningDialog(false); setShowAuthDialog(true); };
  const handleImportAnyway = () => { setShowImportWarningDialog(false); setShowImportDialog(true); };

  // ==========================================================================
  // Guest → Auth transition
  // ==========================================================================
  const getGuestAutomatonData = () => {
    if (!currentAutomaton || !isGuest) return null;
    return { name: currentAutomaton.name || 'Imported from Guest', type: currentAutomaton.type || 'DFA', states: currentAutomaton.states || [], alphabet: currentAutomaton.alphabet || [], transitions: currentAutomaton.transitions || [], language_description: currentAutomaton.language_description || '' };
  };

  const handleAuthSuccess = async () => {
    setShowAuthDialog(false);
    const wantImport = pendingImportAfterAuth;
    setPendingImportAfterAuth(false);
    const data = getGuestAutomatonData();
    if (data && data.states.length > 0) {
      try {
        const { api: apiUtil } = await import('../utils/api');
        const { ok, data: imported } = await apiUtil.post('/automata/import', data);
        if (ok) localStorage.setItem('pendingSelectAutomaton', imported.automaton_id);
      } catch {}
    }
    navigate('/dashboard');
    if (wantImport) setTimeout(() => setShowImportDialog(true), 500);
  };

  const handleGoogleAuthFromGuest = () => {
    const data = getGuestAutomatonData();
    if (data && data.states.length > 0) localStorage.setItem('pendingGuestAutomaton', JSON.stringify(data));
    if (pendingImportAfterAuth) { localStorage.setItem('pendingOpenImportDialog', 'true'); setPendingImportAfterAuth(false); }
    setShowAuthDialog(false);
    loginWithGoogle();
  };

  // ==========================================================================
  // Canvas handlers
  // ==========================================================================
  const handleClearCanvas = (type) => {
    if (blockReadOnly()) { setShowClearCanvasDialog(false); return; }
    const pdaFields = type === 'PDA'
      ? { type: 'PDA', initial_stack_symbol: 'Z', acceptance_mode: 'final_state' }
      : type === 'TM'
      ? { type: 'TM', blank_symbol: '\u25A1', step_limit: 10000 }
      : type ? { type } : {};
    updateCurrentAutomaton({ states: [], transitions: [], alphabet: [], language_description: '', ...pdaFields });
    setSelectedState(null); setSelectedTransition(null);
    canvasRef.current?.resetView();
    setShowClearCanvasDialog(false);
    handleSimReset();
    toast.success('Canvas cleared');
  };

  const handleExportAndClear = async () => { await handleExport(); handleClearCanvas(pendingNewType); };

  const [pendingNewType, setPendingNewType] = useState(null);
  const handleNewAutomatonGuest = (type = 'DFA') => {
    if (currentAutomaton?.states?.length > 0) {
      setPendingNewType(type);
      setShowClearCanvasDialog(true);
    } else {
      handleClearCanvas(type);
    }
  };

  const handleStateCreate = (x, y) => { if (blockReadOnly()) return; addState(snapToGrid ? snap(x) : x, snapToGrid ? snap(y) : y); };
  const handleStateSelect = (stateId) => { setSelectedState(stateId); setSelectedTransition(null); };
  const handleStateMove = (stateId, x, y) => { if (blockReadOnly()) return; updateState(stateId, { x: snapToGrid ? snap(x) : x, y: snapToGrid ? snap(y) : y }); };
  const handleStateRenameInline = (stateId, name) => { if (blockReadOnly()) return; if (name.trim()) updateState(stateId, { name: name.trim() }); };
  const handleTransitionLabelInline = (transitionId, symbols) => { if (blockReadOnly()) return; updateTransition(transitionId, { symbols }); };
  const handleTransitionCurveChange = (transitionId, updates) => { if (blockReadOnly()) return; updateTransition(transitionId, updates); };
  const handleTransitionCreate = (fromId, toId) => {
    if (blockReadOnly()) return null;
    const isPDA = currentAutomaton?.type === 'PDA';
    const t = addTransition(fromId, toId, isPDA ? [] : []);
    return t?.id || null;
  };
  const handleTransitionSelect = (transitionId) => {
    setSelectedTransition(transitionId);
    setSelectedState(null);
  };
  // Called by canvas when user clicks a transition label for editing
  const handleTransitionEditRequest = useCallback((transitionId, screenX, screenY) => {
    if (isImpersonating) return;
    if (currentAutomaton?.type === 'PDA' || currentAutomaton?.type === 'TM') {
      setPdaEditorState({ transitionId, x: screenX, y: screenY });
    }
    // DFA/NFA uses inline editing handled by the canvas
  }, [currentAutomaton?.type, isImpersonating]);
  const handlePdaEditorSave = useCallback((transitionId, rules) => {
    if (blockReadOnly()) return;
    updateTransition(transitionId, { pda_rules: rules });
    setPdaEditorState(null);
  }, [updateTransition, blockReadOnly]);
  const handlePdaEditorDelete = useCallback((transitionId) => {
    if (blockReadOnly()) return;
    deleteTransition(transitionId);
    setPdaEditorState(null);
  }, [deleteTransition, blockReadOnly]);

  // TM transition editor state (reuses pdaEditorState for position)
  const handleTmEditorSave = useCallback((rules) => {
    if (blockReadOnly()) return;
    if (!pdaEditorState) return;
    updateTransition(pdaEditorState.transitionId, { tm_rules: rules });
    setPdaEditorState(null);
  }, [pdaEditorState, updateTransition, blockReadOnly]);

  const handleCanvasClick = () => { setSelectedState(null); setSelectedTransition(null); setPdaEditorState(null); setCanvasContextMenu(null); };

  // Canvas context menu -- every item in this menu mutates the automaton,
  // so while viewing as another user we skip opening it and explain why.
  const handleCanvasContextMenu = useCallback((info) => {
    if (isImpersonating) { blockReadOnly(); return; }
    if (!currentAutomaton) return;
    if (info.type === 'state') {
      const state = currentAutomaton.states.find(s => s.id === info.id);
      if (!state) return;
      const items = buildStateMenuItems(state, currentAutomaton.type === 'TM');
      setCanvasContextMenu({ ...info, items });
    } else if (info.type === 'transition') {
      const items = buildTransitionMenuItems(currentAutomaton.type === 'PDA', currentAutomaton.type === 'TM');
      setCanvasContextMenu({ ...info, items });
    }
  }, [currentAutomaton, isImpersonating, blockReadOnly]);

  const handleContextMenuAction = useCallback((action, id) => {
    if (blockReadOnly()) return;
    if (!currentAutomaton) return;
    if (action === 'set-initial') {
      updateState(id, { is_initial: true });
      toast.success('Set as initial state');
    } else if (action === 'toggle-final') {
      const s = currentAutomaton.states.find(st => st.id === id);
      if (s) {
        updateState(id, { is_final: !s.is_final, ...(s.is_reject ? { is_reject: false } : {}) });
        toast.success(s.is_final ? 'Cleared final state' : 'Set as final state');
      }
    } else if (action === 'toggle-reject') {
      const s = currentAutomaton.states.find(st => st.id === id);
      if (s) {
        updateState(id, { is_reject: !s.is_reject, ...(s.is_final ? { is_final: false } : {}) });
        toast.success(s.is_reject ? 'Cleared reject state' : 'Set as reject state');
      }
    } else if (action === 'rename') {
      // Trigger inline rename via canvas ref
      canvasRef.current?.startEditingState?.(id);
    } else if (action === 'edit') {
      // Edit transition label/rules
      canvasRef.current?.startEditingTransition?.(id);
    } else if (action === 'delete') {
      const isState = currentAutomaton.states.some(s => s.id === id);
      if (isState) {
        deleteState(id); setSelectedState(null);
      } else {
        deleteTransition(id); setSelectedTransition(null);
      }
    }
  }, [currentAutomaton, updateState, deleteState, deleteTransition]);

  // Canvas copy/download — use AutomataCanvas ref methods
  const handleCopyCanvas = () => {
    canvasRef.current?.copyCanvasImage().then(() => {
      toast.success('Automaton image copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy image');
    });
  };

  const handleDownloadPng = () => {
    const name = (currentAutomaton?.name || 'automaton').replace(/[^a-zA-Z0-9_-]/g, '_');
    canvasRef.current?.downloadCanvasImage(`${name}.png`).then(() => {
      toast.success('Image downloaded');
    }).catch(() => {
      toast.error('Failed to download image');
    });
  };

  // Save automaton as Word document
  const handleSaveAsDocx = async (automatonId) => {
    const a = automatonId === currentAutomaton?.automaton_id
      ? currentAutomaton
      : automata.find(x => x.automaton_id === automatonId);
    if (!a) return;
    try {
      let imageBlob = null;
      if (automatonId === currentAutomaton?.automaton_id && a.states?.length > 0) {
        imageBlob = await canvasRef.current?.getCanvasImageBlob();
      }
      await exportAutomatonAsDocx(a, user, imageBlob);
      toast.success('Word document saved');
    } catch (err) {
      console.error('DOCX export error:', err);
      toast.error('Failed to export document');
    }
  };

  // ==========================================================================
  // Simulator
  // ==========================================================================

  // ---- TM simulation ----
  const computeTmSimulation = useCallback((input) => {
    if (!currentAutomaton) return null;
    const initial = currentAutomaton.states.find(s => s.is_initial);
    if (!initial) return { status: 'no-initial', path: [{ states: [], transitions: [], tape: {}, headPos: 0, state: null }] };

    const blankSym = currentAutomaton.blank_symbol || '\u25A1';
    const stepLimit = currentAutomaton.step_limit || 10000;

    // Sparse tape as plain object
    const tape = {};
    for (let i = 0; i < input.length; i++) tape[i] = input[i];

    let headPos = 0;
    let curStateId = initial.id;

    const path = [{
      states: [curStateId],
      transitions: [],
      tape: { ...tape },
      headPos,
      state: curStateId,
    }];

    for (let step = 0; step < stepLimit; step++) {
      const curState = currentAutomaton.states.find(s => s.id === curStateId);

      // Halt: accept
      if (curState?.is_final) return { status: 'accepted', path };
      // Halt: reject
      if (curState?.is_reject) return { status: 'rejected', path };

      const readSym = tape[headPos] || blankSym;

      // Find matching transition + rule
      let matchTrans = null;
      let matchRule = null;
      for (const t of currentAutomaton.transitions) {
        if (t.from_state !== curStateId) continue;
        for (const r of (t.tm_rules || [])) {
          if ((r.read || blankSym) === readSym) { matchTrans = t; matchRule = r; break; }
        }
        if (matchRule) break;
      }

      if (!matchRule) return { status: 'stuck', path };

      // Apply transition
      tape[headPos] = matchRule.write || blankSym;
      if (matchRule.direction === 'L') headPos--;
      else if (matchRule.direction === 'R') headPos++;
      // 'S' = stay

      curStateId = matchTrans.to_state;

      path.push({
        states: [curStateId],
        transitions: [matchTrans.id],
        tape: { ...tape },
        headPos,
        state: curStateId,
      });
    }

    // Step limit reached
    return { status: 'loop', path };
  }, [currentAutomaton]);

  // ---- PDA simulation helpers ----
  const computePdaSimulation = useCallback((input) => {
    if (!currentAutomaton) return null;
    const initial = currentAutomaton.states.find(s => s.is_initial);
    if (!initial) return { status: 'no-initial', path: [{ states: [], transitions: [], stacks: [] }] };

    const z0 = currentAutomaton.initial_stack_symbol || 'Z';
    const acceptMode = currentAutomaton.acceptance_mode || 'final_state';
    const MAX_CONFIGS = 1000;
    const LAMBDA = '\u03BB';
    const EPSILON = '\u03B5';
    const isEmpty = (s) => !s || s === LAMBDA || s === EPSILON;

    let configs = [{ stateId: initial.id, stack: [z0], inputPos: 0 }];
    const path = [];
    let totalExplored = 0;
    let limitHit = false;

    /** Run epsilon closure — ADDS new configs, returns transition ids */
    const epsilonClose = (pos) => {
      const epTransitions = [];
      let changed = true;
      let iter = 0;
      while (changed && iter < 50) {
        changed = false;
        iter++;
        const newConfigs = [];
        for (const cfg of configs) {
          if (cfg.inputPos !== pos) continue;
          for (const t of currentAutomaton.transitions) {
            if (t.from_state !== cfg.stateId) continue;
            for (const r of (t.pda_rules || [])) {
              if (!isEmpty(r.input)) continue;
              const stackTop = cfg.stack.length > 0 ? cfg.stack[cfg.stack.length - 1] : null;
              if (!isEmpty(r.pop) && stackTop !== r.pop) continue;
              const newStack = [...cfg.stack];
              if (!isEmpty(r.pop)) newStack.pop();
              if (!isEmpty(r.push)) {
                for (let k = r.push.length - 1; k >= 0; k--) {
                  if (r.push[k] !== LAMBDA && r.push[k] !== EPSILON) newStack.push(r.push[k]);
                }
              }
              const newCfg = { stateId: t.to_state, stack: newStack, inputPos: pos };
              const key = `${newCfg.stateId}|${newCfg.stack.join(',')}|${pos}`;
              if (!configs.some(c => `${c.stateId}|${c.stack.join(',')}|${c.inputPos}` === key) &&
                  !newConfigs.some(c => `${c.stateId}|${c.stack.join(',')}|${c.inputPos}` === key)) {
                newConfigs.push(newCfg);
                if (!epTransitions.includes(t.id)) epTransitions.push(t.id);
                changed = true;
                totalExplored++;
                if (totalExplored > MAX_CONFIGS) { limitHit = true; break; }
              }
            }
            if (limitHit) break;
          }
          if (limitHit) break;
        }
        configs = [...configs, ...newConfigs];
        if (limitHit) break;
      }
      return epTransitions;
    };

    /** Snapshot current configs at position into a path entry */
    const snapshotPath = (pos, transitions = []) => {
      const posConfigs = configs.filter(c => c.inputPos === pos);
      const states = [...new Set(posConfigs.map(c => c.stateId))];
      const stacks = posConfigs.map(c => [...c.stack]);
      path.push({ states, transitions, stacks });
    };

    // Initial epsilon closure + record path[0]
    epsilonClose(0);
    snapshotPath(0);

    // Process each input symbol
    for (let i = 0; i < input.length; i++) {
      if (limitHit) break;
      const symbol = input[i];
      const nextConfigs = [];
      const stepTransitions = [];
      for (const cfg of configs) {
        if (cfg.inputPos !== i) continue;
        for (const t of currentAutomaton.transitions) {
          if (t.from_state !== cfg.stateId) continue;
          for (const r of (t.pda_rules || [])) {
            if (r.input !== symbol) continue;
            const stackTop = cfg.stack.length > 0 ? cfg.stack[cfg.stack.length - 1] : null;
            if (!isEmpty(r.pop) && stackTop !== r.pop) continue;
            const newStack = [...cfg.stack];
            if (!isEmpty(r.pop)) newStack.pop();
            if (!isEmpty(r.push)) {
              for (let k = r.push.length - 1; k >= 0; k--) {
                if (r.push[k] !== LAMBDA && r.push[k] !== EPSILON) newStack.push(r.push[k]);
              }
            }
            nextConfigs.push({ stateId: t.to_state, stack: newStack, inputPos: i + 1 });
            if (!stepTransitions.includes(t.id)) stepTransitions.push(t.id);
            totalExplored++;
            if (totalExplored > MAX_CONFIGS) { limitHit = true; break; }
          }
          if (limitHit) break;
        }
        if (limitHit) break;
      }

      if (nextConfigs.length === 0 && !limitHit) {
        path.push({ states: [], transitions: stepTransitions, stacks: [] });
        return { status: 'stuck', path, stacks: [] };
      }
      configs = nextConfigs;

      if (i < input.length - 1) {
        // Intermediate: fold epsilon closure into this step
        const epTrans = epsilonClose(i + 1);
        snapshotPath(i + 1, [...stepTransitions, ...epTrans]);
      } else {
        // Last symbol: record WITHOUT epsilon closure first
        snapshotPath(i + 1, stepTransitions);

        // Then step through epsilon transitions as separate path entries
        // Each round: configs that CAN take an epsilon move DO so (replacing themselves)
        let epRound = 0;
        while (epRound < 50 && !limitHit) {
          epRound++;
          const newConfigs = [];
          const replacedIndices = new Set();
          const epTransIds = [];

          for (let ci = 0; ci < configs.length; ci++) {
            const cfg = configs[ci];
            if (cfg.inputPos !== i + 1) continue;
            for (const t of currentAutomaton.transitions) {
              if (t.from_state !== cfg.stateId) continue;
              for (const r of (t.pda_rules || [])) {
                if (!isEmpty(r.input)) continue;
                const stackTop = cfg.stack.length > 0 ? cfg.stack[cfg.stack.length - 1] : null;
                if (!isEmpty(r.pop) && stackTop !== r.pop) continue;
                const newStack = [...cfg.stack];
                if (!isEmpty(r.pop)) newStack.pop();
                if (!isEmpty(r.push)) {
                  for (let k = r.push.length - 1; k >= 0; k--) {
                    if (r.push[k] !== LAMBDA && r.push[k] !== EPSILON) newStack.push(r.push[k]);
                  }
                }
                const newCfg = { stateId: t.to_state, stack: newStack, inputPos: i + 1 };
                const key = `${newCfg.stateId}|${newCfg.stack.join(',')}`;
                const isDup = configs.some((c, idx) => !replacedIndices.has(idx) && c.stateId === newCfg.stateId && c.stack.join(',') === newCfg.stack.join(','))
                  || newConfigs.some(c => c.stateId === newCfg.stateId && c.stack.join(',') === newCfg.stack.join(','));
                if (!isDup) {
                  newConfigs.push(newCfg);
                  replacedIndices.add(ci);
                  if (!epTransIds.includes(t.id)) epTransIds.push(t.id);
                  totalExplored++;
                  if (totalExplored > MAX_CONFIGS) { limitHit = true; break; }
                }
              }
              if (limitHit) break;
            }
            if (limitHit) break;
          }

          if (newConfigs.length === 0) break; // No more epsilon transitions
          // Replace source configs, keep non-transitioning ones
          configs = configs.filter((_, idx) => !replacedIndices.has(idx)).concat(newConfigs);
          const posConfigs = configs.filter(c => c.inputPos === i + 1);
          path.push({
            states: [...new Set(posConfigs.map(c => c.stateId))],
            transitions: epTransIds,
            stacks: posConfigs.map(c => [...c.stack]),
          });
        }
      }
    }

    // Determine acceptance from final configs
    const finalConfigs = configs.filter(c => c.inputPos === input.length);
    let accepted = false;
    if (acceptMode === 'final_state') {
      accepted = finalConfigs.some(c => currentAutomaton.states.find(s => s.id === c.stateId)?.is_final);
    } else {
      accepted = finalConfigs.some(c => c.stack.length === 0);
    }

    const finalStacks = finalConfigs.map(c => [...c.stack]);

    if (limitHit && !accepted) {
      return { status: 'undetermined', path, stacks: finalStacks };
    }
    return { status: accepted ? 'accepted' : 'rejected', path, stacks: finalStacks };
  }, [currentAutomaton]);

  const computeFullSimulation = useCallback((input) => {
    if (!currentAutomaton) return null;
    if (currentAutomaton.type === 'TM') return computeTmSimulation(input);
    if (currentAutomaton.type === 'PDA') return computePdaSimulation(input);

    const initial = currentAutomaton.states.find(s => s.is_initial);
    if (!initial) return { status: 'no-initial', path: [{ states: [], transitions: [] }] };

    // Epsilon closure helper
    const EPSILON = new Set(['\u03BB', '\u03B5', 'epsilon', '\u025B', '']);
    const epsClosure = (stateIds) => {
      const closed = new Set(stateIds);
      const epsTransitions = [];
      const stack = [...stateIds];
      while (stack.length > 0) {
        const sid = stack.pop();
        for (const t of currentAutomaton.transitions) {
          if (t.from_state === sid && t.symbols.some(s => EPSILON.has(s)) && !closed.has(t.to_state)) {
            closed.add(t.to_state);
            epsTransitions.push(t.id);
            stack.push(t.to_state);
          }
        }
      }
      return { states: [...closed], transitions: epsTransitions };
    };

    const initClosure = epsClosure([initial.id]);
    let curStates = initClosure.states;
    const path = [{ states: curStates, transitions: initClosure.transitions }];

    for (let i = 0; i < input.length; i++) {
      const symbol = input[i];
      const nextStates = new Set();
      const stepTransitions = [];
      for (const stateId of curStates) {
        for (const t of currentAutomaton.transitions) {
          if (t.from_state === stateId && t.symbols.includes(symbol)) { nextStates.add(t.to_state); stepTransitions.push(t.id); }
        }
      }
      if (nextStates.size === 0) {
        path.push({ states: [], transitions: stepTransitions });
        return { status: 'stuck', path };
      }
      // Epsilon closure after symbol transitions
      const closure = epsClosure([...nextStates]);
      for (const tid of closure.transitions) stepTransitions.push(tid);
      path.push({ states: closure.states, transitions: stepTransitions });
      curStates = closure.states;
    }
    const anyFinal = curStates.some(id => currentAutomaton.states.find(s => s.id === id)?.is_final);
    return { status: anyFinal ? 'accepted' : 'rejected', path };
  }, [currentAutomaton, computeTmSimulation, computePdaSimulation]);

  // Animation timing constants
  const [simSpeed, setSimSpeed] = useState('slow'); // 'slow' | 'fast'

  const runStepAnimation = (result, fromStep, toStep, reverse = false) => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);

    const sourceStates = fromStep >= 0 ? result.path[fromStep].states : [];
    // When reversing, the transitions that brought us to fromStep are the ones to highlight
    const transIds = reverse ? result.path[fromStep].transitions : result.path[toStep].transitions;
    const targetStates = result.path[toStep].states;
    const displayTarget = targetStates.length > 0 ? targetStates : sourceStates;

    const hasSource = sourceStates.length > 0;
    const hasTrans = transIds.length > 0;

    // Speed multiplier
    const sp = simSpeed === 'fast' ? 1.0 : 2.0;
    const FADE_DUR = 180 * sp;    // source fade out (quick, before transition pulse)
    const PULSE_DUR = 500 * sp;   // transition highlight + fade (single arc)
    const DIM_DUR = 200 * sp;     // transition tail dim
    const RAMP_DUR = 250 * sp;    // destination fade-in
    const TRANS_DELAY = hasSource ? 150 * sp : 0;
    const DEST_DELAY = hasSource ? 330 * sp : (hasTrans ? 80 * sp : 0);

    // Source: fade from 1 → 0 (quadratic ease-out)
    const fadeOut = (elapsed) => {
      if (elapsed >= FADE_DUR) return 0;
      const p = elapsed / FADE_DUR;
      return 1 - p * p;
    };

    // Transition: smooth rise then fall, then dim tail to 0
    const pulseAndDim = (elapsed, delay) => {
      const t = elapsed - delay;
      if (t < 0) return 0;
      if (t >= PULSE_DUR + DIM_DUR) return 0;
      if (t < PULSE_DUR) {
        const p = t / PULSE_DUR;
        return 0.3 + 0.7 * Math.pow(Math.sin(p * Math.PI), 2);
      }
      const d = (t - PULSE_DUR) / DIM_DUR;
      return 0.3 * (1 - d * d);
    };

    // Destination: smooth ease-out ramp from 0 → 1
    const fadeIn = (elapsed, delay) => {
      const t = elapsed - delay;
      if (t < 0) return 0;
      const s = Math.min(1, t / RAMP_DUR);
      return 1 - (1 - s) * (1 - s);
    };

    const totalDur = Math.max(
      hasSource ? FADE_DUR : 0,
      hasTrans ? TRANS_DELAY + PULSE_DUR + DIM_DUR : 0,
      DEST_DELAY + RAMP_DUR
    ) + 30;

    setSimAnimating(true);
    setLastTakenTransitions([]);

    // Set initial animOverride synchronously to prevent a 1-frame flash
    // where simCurrentStates would show target states at full brightness
    const initialGlow = {};
    sourceStates.forEach(id => { initialGlow[id] = 1; }); // source starts at full
    setAnimOverride({ stateGlowMap: initialGlow, transitionGlowMap: {} });

    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;

      if (elapsed >= totalDur) {
        setAnimOverride(null);
        setSimAnimating(false);
        setLastTakenTransitions(transIds);
        animFrameRef.current = null;
        return;
      }

      const stateGlowMap = {};
      const transitionGlowMap = {};

      // Source: just fade out (already highlighted, no pulse)
      sourceStates.forEach(id => { stateGlowMap[id] = fadeOut(elapsed); });
      // Transitions: pulse and dim
      transIds.forEach(id => { transitionGlowMap[id] = pulseAndDim(elapsed, TRANS_DELAY); });
      // Target: fade in (Math.max handles self-loop overlap)
      displayTarget.forEach(id => { stateGlowMap[id] = Math.max(stateGlowMap[id] || 0, fadeIn(elapsed, DEST_DELAY)); });

      setAnimOverride({ stateGlowMap, transitionGlowMap });
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
  };

  const handleSimStep = () => {
    if (simAnimating) return;
    if (simStep === -1) {
      const r = computeFullSimulation(simInput);
      setSimResult(r);
      setSimStep(0);
      runStepAnimation(r, -1, 0);
      return;
    }
    if (simResult && simStep < simResult.path.length - 1) {
      const fromStep = simStep;
      const toStep = simStep + 1;
      setSimStep(toStep);
      runStepAnimation(simResult, fromStep, toStep);
    }
  };
  const handleSimStepBack = () => {
    if (simAnimating || !simResult || simStep <= 0) return;
    const fromStep = simStep;
    const toStep = simStep - 1;
    setSimAutoRun(false);
    setSimStep(toStep);
    runStepAnimation(simResult, fromStep, toStep, true);
  };
  const handleSimRun = () => {
    if (simAnimating) {
      if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
      setAnimOverride(null);
      setSimAnimating(false);
    }
    setLastTakenTransitions([]);
    if (simStep === -1) { const r = computeFullSimulation(simInput); setSimResult(r); setSimStep(0); setSimAutoRun(true); return; }
    setSimAutoRun(true);
  };
  const handleSimReset = () => {
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
    setAnimOverride(null);
    setSimAnimating(false);
    setLastTakenTransitions([]);
    setSimResult(null);
    setSimStep(-1);
    setSimAutoRun(false);
  };

  useEffect(() => {
    if (!simAutoRun || !simResult) return;
    if (simStep >= simResult.path.length - 1) { setSimAutoRun(false); return; }
    const timer = setTimeout(() => setSimStep(s => s + 1), 450);
    return () => clearTimeout(timer);
  }, [simAutoRun, simStep, simResult]);

  const simFinished = simResult != null && simStep >= simResult.path.length - 1;
  const simCurrentStates = simResult && simStep >= 0
    ? (simResult.path[simStep].states.length > 0 ? simResult.path[simStep].states : simStep > 0 ? simResult.path[simStep - 1].states : [])
    : [];
  const simActiveTransitions = simResult && simStep >= 0 ? simResult.path[simStep].transitions : [];
  const simHighlightStatus = simResult && simStep >= 0 ? (simFinished && !simAnimating ? simResult.status : null) : null;

  // Compute which states should glow green on acceptance
  const simAcceptedStateIds = (() => {
    if (simHighlightStatus !== 'accepted') return [];
    if (currentAutomaton?.type === 'PDA' && currentAutomaton?.acceptance_mode === 'empty_stack') {
      // Green only for states whose stack is empty
      const step = simResult?.path?.[simStep];
      if (!step) return [];
      const accepted = [];
      (step.states || []).forEach((sid, i) => {
        const stack = step.stacks?.[i];
        if (stack && stack.length === 0) accepted.push(sid);
      });
      return accepted;
    }
    // Default: green only for final states among current states
    return simCurrentStates.filter(id => currentAutomaton?.states?.find(s => s.id === id)?.is_final);
  })();

  // Animation overrides for step-through glow
  const effectiveHighlightedStates = animOverride ? [] : simCurrentStates;
  const effectiveActiveTransitions = animOverride ? [] : (lastTakenTransitions.length > 0 ? [] : simActiveTransitions);
  const effectiveStateGlowMap = animOverride?.stateGlowMap ?? null;
  const effectiveTransitionGlowMap = animOverride?.transitionGlowMap ?? null;
  const effectiveLastTakenTransitions = animOverride ? [] : lastTakenTransitions;

  // Cleanup animation on unmount
  useEffect(() => {
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
  }, []);

  // ==========================================================================
  // Verification
  // ==========================================================================
  const handleVerify = useCallback(() => {
    const result = verifyAutomaton(verifyMode);
    setVerificationResult(result);
    setShowVerifyDialog(true);
  }, [verifyAutomaton, verifyMode]);

  const handleVerifyModeChange = (mode) => {
    setVerifyMode(mode);
    setVerificationResult(verifyAutomaton(mode));
  };

  // ==========================================================================
  // Context menu
  // ==========================================================================
  const handleAutomatonContextMenu = (e, automatonId) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, automatonId });
  };

  // ==========================================================================
  // Sidebar group dialog openers
  // ==========================================================================
  const handleShowGroupRenameDialog = (groupId, name) => {
    setGroupRenameId(groupId);
    setGroupRenameName(name);
    setShowGroupRenameDialog(true);
  };

  const handleShowImportDialog = (groupId) => {
    setImportGroupId(groupId || null);
    setShowImportDialog(true);
  };

  const handleExportGroup = (groupId, groupName) => {
    const groupAut = automata.filter(a => a.group_id === groupId);
    if (groupAut.length === 0) { toast.error('No automata in this group'); return; }
    const exportItems = groupAut.map(a => {
      const item = {
        name: a.name, type: a.type, states: a.states, alphabet: a.alphabet,
        transitions: a.transitions, language_description: a.language_description || '',
        test_accept_strings: a.test_accept_strings || [], test_reject_strings: a.test_reject_strings || [],
      };
      if (a.type === 'PDA') { item.initial_stack_symbol = a.initial_stack_symbol || 'Z'; item.acceptance_mode = a.acceptance_mode || 'final_state'; }
      if (a.type === 'TM') { item.blank_symbol = a.blank_symbol || '\u25A1'; item.step_limit = a.step_limit || 10000; }
      return item;
    });
    const blob = new Blob([JSON.stringify({ type: 'automata_group', group_name: groupName, automata: exportItems }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `${groupName.replace(/[^a-zA-Z0-9_-]/g, '_')}.json`; link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${exportItems.length} automata`);
  };

  // ==========================================================================
  // Loading state
  // ==========================================================================
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <TooltipProvider delayDuration={300}>
      <div className="h-full flex flex-col overflow-hidden bg-background text-foreground">
        <DashboardHeader
          isGuest={isGuest}
          currentAutomaton={currentAutomaton}
          onShowAuthDialog={() => setShowAuthDialog(true)}
          onNewAutomaton={handleNewAutomatonGuest}
          onExport={handleExport}
          onGuestImportClick={handleGuestImportClick}
          onToggleHelp={() => setShowHelpPane(prev => !prev)}
        />

        <div className="flex-1 flex overflow-hidden relative">
          {/* Sidebar (authenticated users only) */}
          {!isGuest && (
            <Sidebar
              groups={groups}
              automata={automata}
              currentAutomaton={currentAutomaton}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              sidebarWidth={sidebarWidth}
              setSidebarWidth={setSidebarWidth}
              onNewAutomaton={handleNewAutomaton}
              onLoadAutomaton={handleLoadAutomaton}
              onDeleteAutomaton={handleDeleteAutomaton}
              onDuplicateAutomaton={handleDuplicateAutomaton}
              onExportAutomaton={handleExportAutomaton}
              onExportGroup={handleExportGroup}
              onSaveAsDocx={handleSaveAsDocx}
              onOpenRenameDialog={openSidebarRenameDialog}
              onShowGroupRenameDialog={handleShowGroupRenameDialog}
              onShowNewGroupDialog={() => setShowNewGroupDialog(true)}
              onShowImportDialog={handleShowImportDialog}
              onDeleteGroup={deleteGroup}
              onContextMenu={handleAutomatonContextMenu}
              onMoveAutomaton={moveAutomaton}
              onReorderAutomata={reorderAutomata}
              onReorderGroups={reorderGroups}
              onTogglePin={togglePinAutomaton}
              isReadOnly={isImpersonating}
            />
          )}

          {/* Main content area */}
          <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {showSimulator && (
              <SimulatorBar
                currentAutomaton={currentAutomaton}
                simInput={simInput}
                setSimInput={setSimInput}
                simResult={simResult}
                simStep={simStep}
                simFinished={simFinished}
                simCurrentStates={simCurrentStates}
                simAutoRun={simAutoRun}
                simAnimating={simAnimating}
                simSpeed={simSpeed}
                onSpeedChange={setSimSpeed}
                onStepBack={handleSimStepBack}
                onStep={handleSimStep}
                onRun={handleSimRun}
                onReset={handleSimReset}
                onClose={() => { setShowSimulator(false); handleSimReset(); }}
              />
            )}

            {/* Canvas */}
            <div className="flex-1 relative min-h-0 overflow-hidden">
              {currentAutomaton && (
                <>
                  <AutomataCanvas
                    ref={canvasRef}
                    states={currentAutomaton.states}
                    transitions={currentAutomaton.transitions}
                    selectedState={selectedState}
                    selectedTransition={selectedTransition}
                    isPDA={currentAutomaton.type === 'PDA'}
                    isTM={currentAutomaton.type === 'TM'}
                    onStateCreate={handleStateCreate}
                    onStateSelect={handleStateSelect}
                    onStateMove={handleStateMove}
                    onStateRenameInline={handleStateRenameInline}
                    onTransitionCreate={handleTransitionCreate}
                    onTransitionSelect={handleTransitionSelect}
                    onTransitionCurveChange={handleTransitionCurveChange}
                    onTransitionLabelInline={handleTransitionLabelInline}
                    onTransitionEditRequest={handleTransitionEditRequest}
                    onStateDelete={(id) => { if (blockReadOnly()) return; deleteState(id); setSelectedState(null); }}
                    onTransitionDelete={(id) => { if (blockReadOnly()) return; deleteTransition(id); setSelectedTransition(null); }}
                    highlightedStates={effectiveHighlightedStates}
                    activeTransitions={effectiveActiveTransitions}
                    stateGlowMap={effectiveStateGlowMap}
                    transitionGlowMap={effectiveTransitionGlowMap}
                    lastTakenTransitions={effectiveLastTakenTransitions}
                    simulationStatus={simHighlightStatus}
                    simAcceptedStateIds={simAcceptedStateIds}
                    onCanvasClick={handleCanvasClick}
                    onContextMenu={handleCanvasContextMenu}
                    onZoomChange={setCanvasZoom}
                    isReadOnly={isImpersonating}
                  />

                  {/* Context menu */}
                  <CanvasContextMenu
                    menu={canvasContextMenu}
                    onAction={handleContextMenuAction}
                    onClose={() => setCanvasContextMenu(null)}
                  />

                  {/* PDA Transition Editor popup */}
                  {pdaEditorState && currentAutomaton.type === 'PDA' && (() => {
                    const t = currentAutomaton.transitions.find(tr => tr.id === pdaEditorState.transitionId);
                    if (!t) return null;
                    return (
                      <PdaTransitionEditor
                        transition={t}
                        states={currentAutomaton.states}
                        position={{ x: pdaEditorState.x, y: pdaEditorState.y }}
                        onSave={handlePdaEditorSave}
                        onCancel={() => setPdaEditorState(null)}
                        onDelete={handlePdaEditorDelete}
                      />
                    );
                  })()}

                  {/* TM Transition Editor popup */}
                  {pdaEditorState && currentAutomaton.type === 'TM' && (() => {
                    const t = currentAutomaton.transitions.find(tr => tr.id === pdaEditorState.transitionId);
                    if (!t) return null;
                    const fromState = currentAutomaton.states.find(s => s.id === t.from_state);
                    const toState = currentAutomaton.states.find(s => s.id === t.to_state);
                    return (
                      <TmTransitionEditor
                        transition={t}
                        fromStateName={fromState?.name || '?'}
                        toStateName={toState?.name || '?'}
                        position={{ x: pdaEditorState.x, y: pdaEditorState.y }}
                        onSave={handleTmEditorSave}
                        onCancel={() => setPdaEditorState(null)}
                      />
                    );
                  })()}

                  {/* Floating auxiliary memory overlays */}
                  {currentAutomaton.type === 'PDA' && (
                    <FloatingStack simResult={simResult} simStep={simStep} states={currentAutomaton.states} />
                  )}
                  {currentAutomaton.type === 'TM' && (
                    <FloatingTape simResult={simResult} simStep={simStep} states={currentAutomaton.states} simInput={simInput} />
                  )}

                  <HintsPanel visible={showHintsPanel} onToggleVisible={() => setShowHintsPanel(false)} />

                  {!showHintsPanel && (
                    <button
                      onClick={() => setShowHintsPanel(true)}
                      className="absolute bottom-4 left-4 glass-panel rounded-lg p-2.5 hover:bg-muted/50 transition-colors z-20"
                      data-testid="show-hints-btn"
                    >
                      <HelpCircle className="w-5 h-5 text-primary" />
                    </button>
                  )}

                  <CanvasToolbar
                    canvasRef={canvasRef}
                    canvasZoom={canvasZoom}
                    canUndo={canUndo && !isImpersonating}
                    canRedo={canRedo && !isImpersonating}
                    onUndo={undo}
                    onRedo={redo}
                    snapToGrid={snapToGrid}
                    setSnapToGrid={setSnapToGrid}
                    showSimulator={showSimulator}
                    setShowSimulator={setShowSimulator}
                    onVerify={handleVerify}
                    onCopyCanvas={handleCopyCanvas}
                    onDownloadPng={handleDownloadPng}
                    currentAutomatonName={currentAutomaton?.name}
                  />
                </>
              )}
            </div>

            {/* Bottom Panels with resize handle */}
            {currentAutomaton && (
              <div className="shrink-0">
                {/* Resize handle / toggle bar */}
                <div
                  className={`border-t border-border flex items-center justify-center gap-1.5 ${showBottomPanels ? 'cursor-row-resize hover:bg-primary/10 active:bg-primary/20' : 'cursor-pointer hover:bg-muted/50'} transition-colors select-none group`}
                  style={{ height: '14px' }}
                  data-testid="panel-resize-handle"
                  onClick={(e) => {
                    if (!showBottomPanels) { setShowBottomPanels(true); return; }
                  }}
                  onDoubleClick={() => setShowBottomPanels(!showBottomPanels)}
                  onMouseDown={(e) => {
                    if (!showBottomPanels) return;
                    e.preventDefault();
                    const startY = e.clientY;
                    const startH = panelHeight;
                    const onMove = (me) => {
                      const newH = Math.max(80, Math.min(500, startH - (me.clientY - startY)));
                      setPanelHeight(newH);
                    };
                    const onUp = () => {
                      window.removeEventListener('mousemove', onMove);
                      window.removeEventListener('mouseup', onUp);
                    };
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                  }}
                >
                  <div className="w-8 h-1 rounded-full bg-border group-hover:bg-primary/40 transition-colors" />
                  {showBottomPanels ? (
                    <ChevronDown className="w-3 h-3 text-muted-foreground group-hover:text-primary/60 transition-colors" />
                  ) : (
                    <ChevronUp className="w-3 h-3 text-muted-foreground group-hover:text-primary/60 transition-colors" />
                  )}
                </div>
                {showBottomPanels && (
                  <BottomPanels
                    height={panelHeight}
                    currentAutomaton={currentAutomaton}
                    updateCurrentAutomaton={guardedUpdateCurrentAutomaton}
                    copyLanguageDescription={copyLanguageDescription}
                    copySpecification={copySpecification}
                    copyTransitionFunction={copyTransitionFunction}
                    onOpenSimulator={(str) => {
                      setSimInput(str === '\u03BB' || str === '\u03B5' ? '' : str);
                      setShowSimulator(true);
                      handleSimReset();
                    }}
                    simResult={simResult}
                    simStep={simStep}
                    isReadOnly={isImpersonating}
                  />
                )}
              </div>
            )}
          </main>

          {/* Help pane (right side) */}
          <HelpPane open={showHelpPane} onClose={() => setShowHelpPane(false)} />
        </div>

        {/* Saving indicator */}
        {saving && (
          <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 shadow-lg text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Saving...
          </div>
        )}

        {/* Bug report */}
        {!isGuest && <BugReportButton />}

        {/* All dialogs */}
        <DashboardDialogs
          showRenameDialog={showRenameDialog} setShowRenameDialog={setShowRenameDialog}
          renameName={renameName} setRenameName={setRenameName} onRenameSubmit={handleRenameSubmit}
          showSidebarRenameDialog={showSidebarRenameDialog} setShowSidebarRenameDialog={setShowSidebarRenameDialog}
          sidebarRenameName={sidebarRenameName} setSidebarRenameName={setSidebarRenameName} onSidebarRenameSubmit={handleSidebarRenameSubmit}
          showImportDialog={showImportDialog} setShowImportDialog={setShowImportDialog}
          importJson={importJson} setImportJson={setImportJson}
          importGroupId={importGroupId} setImportGroupId={setImportGroupId}
          groups={groups} onImportSubmit={handleImportSubmit} onImportFile={handleImportFile}
          showImportWarningDialog={showImportWarningDialog} setShowImportWarningDialog={setShowImportWarningDialog}
          onExportThenImport={handleExportThenImport} onSignInThenImport={handleSignInThenImport} onImportAnyway={handleImportAnyway}
          showClearCanvasDialog={showClearCanvasDialog} setShowClearCanvasDialog={setShowClearCanvasDialog}
          onClearCanvas={() => handleClearCanvas(pendingNewType)} onExportAndClear={handleExportAndClear}
          showAuthDialog={showAuthDialog} setShowAuthDialog={setShowAuthDialog}
          onAuthSuccess={handleAuthSuccess} onGoogleAuth={handleGoogleAuthFromGuest}
          showVerifyDialog={showVerifyDialog} setShowVerifyDialog={setShowVerifyDialog}
          verificationResult={verificationResult} verifyMode={verifyMode} setVerifyMode={setVerifyMode}
          verifyAutomaton={handleVerifyModeChange}
          showNewGroupDialog={showNewGroupDialog} setShowNewGroupDialog={setShowNewGroupDialog} createGroup={createGroup}
          showGroupRenameDialog={showGroupRenameDialog} setShowGroupRenameDialog={setShowGroupRenameDialog}
          groupRenameId={groupRenameId} groupRenameName={groupRenameName} setGroupRenameName={setGroupRenameName}
          renameGroup={renameGroup} deleteGroup={deleteGroup}
          contextMenu={contextMenu} onCloseContextMenu={closeContextMenu}
          automata={automata}
          onContextRename={openSidebarRenameDialog} onContextDuplicate={handleDuplicateAutomaton}
          onContextExport={handleExportAutomaton} onContextSaveDocx={handleSaveAsDocx}
          onContextDelete={handleDeleteAutomaton} onContextTogglePin={togglePinAutomaton}
          isReadOnly={isImpersonating}
        />
      </div>
    </TooltipProvider>
  );
}
